import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApplicationFormPageEnum } from '../types';
import { AddCard } from '@pages/ApplicationFormPage/AddCard/AddCard';
import { Cards } from '../Cards/Cards';
import { CardsWrapperType, ICard } from './types';
import { useIntl } from 'react-intl';
import { addCardQuestions } from '../AddCard/questions';
import { getCurrentRoute } from '@utils/location';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { IApplicationInProgressQuestion } from '../types';
import { parseApplicationInProgress } from '../helpers';

export const CardsWrapper = () => {
  const intl = useIntl();
  const { id = '' } = useParams();
  const { data: applicationInProgress, refetch, isLoading } = useApplicationInProgressById(id);
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress, {
    onSuccess: refetch,
  });
  const currentRoute = getCurrentRoute();
  const [cards, setCards] = useState<ICard[]>([]);
  const [currentPage, setCurrentPage] = useState<CardsWrapperType>(ApplicationFormPageEnum.CARDS);
  const [cardToEdit, setCardToEdit] = useState<ICard | null>(null);
  const isEdit = !!cardToEdit;

  const cardFormatter = (card: ICard): IApplicationInProgressQuestion[] => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...cardDetails } = card;
    const isAPartOfFullName = ['firstName', 'lastName'];

    return Object.entries(cardDetails).map(([key, value]) => {
      return {
        question: intl.formatMessage({
          id: addCardQuestions[isAPartOfFullName.includes(key) ? 'fullName' : key].labelKey,
        }),
        key,
        answer: value,
      };
    });
  };

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const formattedResponse = applicationInProgress[currentRoute].map((card: any) => ({
        id: uuidv4(),
        ...parseApplicationInProgress(card),
      })) as ICard[];

      setCards(formattedResponse);
    }
  }, [applicationInProgress]);

  const patchCards = (cardsList: ICard[]) => {
    const questions = cardsList.map(cardFormatter);

    patchApplicationInProgress({ questions, currentRoute, applicationId: id });
  };

  const editCardRedirectHandler = (card: ICard) => {
    setCardToEdit(card);
    setCurrentPage(ApplicationFormPageEnum.ADD_CARD);
  };

  const addCardHandler = (card: ICard) => {
    patchCards([...cards, card]);
    setCurrentPage(ApplicationFormPageEnum.CARDS);
  };

  const goBackHandler = () => {
    setCurrentPage(ApplicationFormPageEnum.CARDS);
    setCardToEdit(null);
  };

  const editCardHandler = (card: ICard) => {
    const newCardsList = cards.map(cardUnit => {
      return cardUnit.id === card.id ? card : cardUnit;
    });

    patchCards(newCardsList);
    goBackHandler();
  };

  const removeCardByIdHandler = async (id: string) => {
    const newCardsList = cards.filter(card => card.id !== id);

    patchCards(newCardsList);
  };

  const submitCardHandler = (card: ICard) => (isEdit ? editCardHandler(card) : addCardHandler(card));

  const ActiveFormPage = () => {
    switch (currentPage) {
      case ApplicationFormPageEnum.CARDS:
        return (
          <Cards
            cards={cards}
            onAddCardRedirect={() => setCurrentPage(ApplicationFormPageEnum.ADD_CARD)}
            onEditCardRedirect={editCardRedirectHandler}
            onRemoveCardById={removeCardByIdHandler}
          />
        );
      case ApplicationFormPageEnum.ADD_CARD:
        return <AddCard cardToEdit={cardToEdit} onSubmitCard={submitCardHandler} onGoBack={goBackHandler} />;
      default:
        return null;
    }
  };

  return isLoading ? null : <ActiveFormPage />;
};
